package one.healthium.today.ui.carni.components

import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.animation.core.tween
import androidx.compose.foundation.Image
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.unit.dp
import ifaandroid.feature.today_v2.today_ui_carni.generated.resources.Res
import ifaandroid.feature.today_v2.today_ui_carni.generated.resources.ic_charm_lightning_bolt
import loco.GENERAL_CARBO
import loco.GENERAL_FAT
import loco.GENERAL_FIBRE
import loco.GENERAL_PROTEIN
import loco.GENERAL_TODAY
import one.healthium.base.utils.Formatters.formatCalories
import one.healthium.base.utils.Formatters.formatGrams
import one.healthium.base.utils.orZero
import one.healthium.loco.resolve
import one.healthium.today.ui.calories_counter.CaloriesCounterUiModel
import one.healthium.today.ui.calories_counter.NutritionGoalUiModel
import one.healthium.ui.compose.theme.AppTheme
import one.healthium.ui.compose.theme.gray_16_20
import one.healthium.ui.compose.theme.green_3
import one.healthium.ui.compose.theme.red_1
import one.healthium.ui.compose.theme.white
import one.healthium.ui.compose.theme.yellow
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource
import androidx.compose.ui.tooling.preview.Preview

@Composable
internal fun CaloriesCounter(
    modifier: Modifier = Modifier,
    model: CaloriesCounterUiModel?,
) {
    Row(
        modifier = modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.Center,
        verticalAlignment = Alignment.CenterVertically,
    ) {
        CaloriesProgress(model?.caloriesGoal)
        Spacer(Modifier.width(24.dp))
        Column {
            CarboProgress(model?.carbohydratesGoal)
            Spacer(Modifier.height(6.dp))
            FatProgress(model?.fatGoal)
            Spacer(Modifier.height(6.dp))
            ProteinProgress(model?.proteinGoal)
            Spacer(Modifier.height(6.dp))
            FibreProgress(model?.fiberGoal)
        }
    }
}

@Composable
internal fun CaloriesProgress(goal: NutritionGoalUiModel?) {
    val animatedProgress by animateFloatAsState(
        targetValue = goal?.progress ?: 0f,
        animationSpec = tween(durationMillis = 400, delayMillis = 200),
    )

    Box(
        contentAlignment = Alignment.Center,
    ) {
        CircularProgressIndicator(
            modifier = Modifier.size(184.dp),
            color = AppTheme.colors.tertiary,
            trackColor = gray_16_20,
            strokeWidth = 20.dp,
            strokeCap = StrokeCap.Round,
            progress = { animatedProgress },
        )
        Column(
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            Image(
                painter = painterResource(Res.drawable.ic_charm_lightning_bolt),
                contentDescription = null,
            )
            Spacer(Modifier.height(8.dp))
            Text(
                modifier = Modifier.testTag("calories_progress"),
                text = goal?.amount.formatCalories().resolve(),
                style = AppTheme.textStyles.poppins20,
                color = AppTheme.colors.textInverted100,
            )
            Text(
                text = stringResource(loco.Res.string.GENERAL_TODAY),
                style = AppTheme.textStyles.secondary_12_400,
                color = AppTheme.colors.textInverted100.copy(alpha = 0.75f),
            )
        }
    }
}

@Composable
private fun CarboProgress(goal: NutritionGoalUiModel?) {
    NutritionalGoalItem(
        goal = goal,
        name = stringResource(loco.Res.string.GENERAL_CARBO),
        color = yellow,
    )
}

@Composable
private fun ProteinProgress(goal: NutritionGoalUiModel?) {
    NutritionalGoalItem(
        goal = goal,
        name = stringResource(loco.Res.string.GENERAL_PROTEIN),
        color = white,
    )
}

@Composable
private fun FatProgress(goal: NutritionGoalUiModel?) {
    NutritionalGoalItem(
        goal = goal,
        name = stringResource(loco.Res.string.GENERAL_FAT),
        color = green_3
    )
}

@Composable
private fun FibreProgress(goal: NutritionGoalUiModel?) {
    NutritionalGoalItem(
        goal = goal,
        name = stringResource(loco.Res.string.GENERAL_FIBRE),
        color = red_1
    )
}

@Composable
private fun NutritionalGoalItem(
    goal: NutritionGoalUiModel?,
    name: String,
    color: Color,
) {
    Row(
        verticalAlignment = Alignment.CenterVertically,
    ) {
        CircularProgressIndicator(
            modifier = Modifier.size(28.dp),
            color = color,
            trackColor = gray_16_20,
            strokeWidth = 4.dp,
            strokeCap = StrokeCap.Square,
            progress = { goal?.progress ?: 0f },
        )
        Spacer(Modifier.width(14.dp))
        Column {
            NutritionGoalItemValue(goal)
            Text(
                text = name,
                style = AppTheme.textStyles.secondary_12_400,
                color = AppTheme.colors.textInverted100.copy(alpha = 0.5f),
            )
        }
    }
}

@Composable
private fun NutritionGoalItemValue(goal: NutritionGoalUiModel?) {
    Text(
        text = goal?.amount.orZero().formatGrams().resolve(),
        style = AppTheme.textStyles.secondary_17_400,
        color = AppTheme.colors.textInverted100,
    )
}

@Composable
@Preview(showBackground = true)
private fun CaloriesCounterPreview() {
    AppTheme {
        CaloriesCounter(
            model = CaloriesCounterUiModel(
                caloriesGoal = NutritionGoalUiModel(2000f, 0.5f),
                carbohydratesGoal = NutritionGoalUiModel(300f, 0.3f),
                proteinGoal = NutritionGoalUiModel(150f, 0.4f),
                fatGoal = NutritionGoalUiModel(70f, 0.6f),
                fiberGoal = NutritionGoalUiModel(30f, 0.2f)
            )
        )
    }
}
